import { FC, useEffect, useRef } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  matchPath,
  useNavigate,
} from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'src/utils/axios';
import { RootState } from 'src/redux/rootReducerType';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import {
  setAccountHubUser,
  clearElevatedAuth,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import ElevatedAuthGate from './ElevatedAuthGate';
import { canManageHousehold, isInSameHousehold } from './accountHubUtils';

const USER_PATTERN = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/:userId`;
const USER_CHILD_PATTERN = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/:userId/*`;

const AccountHubRouter: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const userHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.userHouseholds
  );
  const elevatedAuthMethod = useSelector(
    (state: RootState) =>
      state.newRenterInterfaceUser.accountHub.elevatedAuth.method
  );
  const location = useLocation();
  const hubRoot = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`;

  // Intercept 403 totp_elevation_required responses for any as_user_id call
  // and redirect back to the hub root so the "Re-verify with authenticator"
  // gate is shown instead of a raw error.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const hubRootRef = useRef(hubRoot);
  hubRootRef.current = hubRoot;
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        const errorCode = (
          err as { response?: { data?: { error_code?: string } } }
        )?.response?.data?.error_code;
        if (status === 403 && errorCode === 'totp_elevation_required') {
          dispatchRef.current(clearElevatedAuth());
          navigateRef.current(hubRootRef.current, { replace: true });
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  // Keep selectedUserId in sync with the :userId path segment so WS guards
  // can filter events to the user currently being viewed.
  useEffect(() => {
    const match = matchPath({ path: USER_PATTERN }, location.pathname);
    const rawId = match?.params?.userId;
    const parsed = rawId ? Number(rawId) : null;
    dispatch(
      setAccountHubUser(parsed && !Number.isNaN(parsed) ? parsed : null)
    );
  }, [dispatch, location.pathname]);

  if (!user) {
    return null;
  }

  const indexPattern = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`;
  const isAtIndex =
    matchPath({ path: indexPattern, end: true }, location.pathname) !== null;

  if (isAtIndex) {
    const elevated = canManageHousehold(userHouseholds);
    if (!elevated) {
      return (
        <Navigate
          to={`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${String(
            user.id
          )}`}
          replace
        />
      );
    }
  }

  // ── Per-user child-route access guard ──────────────────────────────────────
  // On both desktop and mobile, child routes under /:userId/* render their
  // Outlet directly without first passing through AccountHubPage, so the
  // inline access checks there don't fire before child pages mount.
  //
  // Enforce here, at the router boundary, so no child route can mount until
  // both conditions are satisfied:
  //   1. Scope — currentUser is `userId` themselves, OR is a helper/HoH/Co-Head
  //              sharing the same household.
  //   2. TOTP elevation — when accessing another user's sub-pages, the elevated
  //              session must have used the authenticator method (not OTP).
  //
  // Note: the index route /:userId (AccountHubPage) is intentionally excluded
  // from the TOTP check so the "Re-verify with authenticator" prompt there
  // remains reachable.
  const childMatch =
    matchPath({ path: USER_CHILD_PATTERN }, location.pathname) ??
    matchPath({ path: USER_PATTERN, end: true }, location.pathname);
  if (childMatch) {
    const rawId = childMatch.params.userId;
    const targetUserId = rawId ? Number(rawId) : null;

    if (targetUserId !== null && !Number.isNaN(targetUserId)) {
      const isSelf = targetUserId === user.id;

      if (!isSelf) {
        // Scope check: must be a helper/HoH/co-head in the same household.
        const elevated = canManageHousehold(userHouseholds);
        const sameHousehold = isInSameHousehold(userHouseholds, targetUserId);
        if (!elevated || !sameHousehold) {
          return <Navigate to={`${hubRoot}/${String(user.id)}`} replace />;
        }

        // Block only the 2FA sub-routes for proxy mode — the login-security
        // page itself remains accessible so helpers can see it. 2FA setup,
        // management, backup codes, and sessions are personal and must only
        // be reached by the account owner.
        const tfaSubPattern = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/:userId/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}/*`;
        const tfaSubExact = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/:userId/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}`;
        const backupCodesPattern = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/:userId/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_BACKUP_CODES}`;
        if (
          matchPath({ path: tfaSubPattern }, location.pathname) ||
          matchPath({ path: tfaSubExact, end: true }, location.pathname) ||
          matchPath({ path: backupCodesPattern, end: true }, location.pathname)
        ) {
          return (
            <Navigate
              to={`${hubRoot}/${String(targetUserId)}/${
                RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY
              }`}
              replace
            />
          );
        }

        // TOTP-elevation check for child pages (non-index): helper must have
        // verified via their authenticator app, not just OTP.
        const isChildRoute =
          matchPath({ path: USER_CHILD_PATTERN }, location.pathname) !== null;
        if (isChildRoute && elevatedAuthMethod !== 'totp') {
          return <Navigate to={hubRoot} replace />;
        }
      }
    }
  }

  return (
    <ElevatedAuthGate>
      <Outlet />
    </ElevatedAuthGate>
  );
};

export default AccountHubRouter;
